import { Box, Paper, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import ComponentLoader from '@commons/DeepsightComponents/ComponentLoader';

import { getMetricOptions } from './utils/config';
import ChartTooltip from '../common/ChartTooltip';

const ProductTimeSeriesChart = ({
  chartData,
  currentPeriod,
  initialSelectedProduct,
  initialSelectedMetric,
  isLoadingChart,
  previousPeriod,
  productTableData,
}) => {
  const metricOptions = getMetricOptions();
  const [selectedProductForChart, setSelectedProductForChart] = useState(null);
  const [selectedMetricForChart, setSelectedMetricForChart] = useState(metricOptions[1]);

  const theme = getTheme();

  // Initialize selected product when products are available or when initial values change
  useEffect(() => {
    if (initialSelectedProduct) {
      setSelectedProductForChart(initialSelectedProduct);
    } else if (productTableData && productTableData.length > 0 && !selectedProductForChart) {
      setSelectedProductForChart(
        productTableData.reduce((max, current) =>
          current.turnoverExcludingTaxes.current > max.turnoverExcludingTaxes.current
            ? current
            : max,
        ),
      );
    }
  }, [productTableData, initialSelectedProduct]);

  // Initialize selected metric when initial value changes
  useEffect(() => {
    if (initialSelectedMetric) {
      setSelectedMetricForChart(initialSelectedMetric);
    }
  }, [initialSelectedMetric]);

  return (
    <Paper sx={{ p: 3, height: 500, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('ANALYSIS.PERFORMANCE.PRODUCT_TIME_SERIES_CHART_TITLE')}
        </Typography>

        {/* Filtres */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Dropdown Produits */}
          <Dropdown
            customStyle={{
              width: '300px',
            }}
            isRequired={true}
            isUniqueSelection={true}
            items={productTableData || []}
            label={i18next.t('GENERAL.PRODUCT')}
            searchPlaceholder={i18next.t('GENERAL.SEARCH')}
            selectedItem={selectedProductForChart}
            onSelectionChange={setSelectedProductForChart}
          />

          {/* Dropdown Métrique */}
          <Dropdown
            customStyle={{
              width: '200px',
            }}
            isRequired={true}
            isSearchDisabled={true}
            isUniqueSelection={true}
            items={metricOptions || []}
            label={i18next.t('ANALYSIS.PERFORMANCE.METRIC_DROPDOWN_LABEL')}
            selectedItem={selectedMetricForChart}
            onSelectionChange={setSelectedMetricForChart}
          />
        </Box>
      </Box>

      {/* Line Chart */}
      {selectedProductForChart && selectedMetricForChart ? (
        isLoadingChart ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: 350,
              justifyContent: 'center',
            }}
          >
            <ComponentLoader height={50} width={50} />
          </Box>
        ) : (
          <ResponsiveContainer height={350} width="100%">
            <LineChart data={chartData} margin={{ bottom: 5, left: 20, right: 20, top: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="formattedDates.current" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                content={
                  <ChartTooltip
                    options={metricOptions}
                    payloadLabel="formattedDates"
                    selectedMetric={selectedMetricForChart}
                  />
                }
              />
              <Legend />

              <Line
                activeDot={{ r: 6, stroke: theme.colors.greys.medium, strokeWidth: 2 }}
                dataKey={`previous.${selectedMetricForChart.id}.${selectedProductForChart.id}`}
                dot={{ fill: theme.colors.greys.medium, strokeWidth: 2, r: 4 }}
                name={i18next.t('ANALYSIS.PERFORMANCE.PREVIOUS_PERIOD')}
                stroke={theme.colors.greys.medium}
                strokeWidth={3}
                type="monotone"
              />
              <Line
                activeDot={{ r: 6, stroke: theme.colors.blue, strokeWidth: 2 }}
                dataKey={`current.${selectedMetricForChart.id}.${selectedProductForChart.id}`}
                dot={{ fill: theme.colors.blue, strokeWidth: 2, r: 4 }}
                name={i18next.t('ANALYSIS.PERFORMANCE.CURRENT_PERIOD')}
                stroke={theme.colors.blue}
                strokeWidth={3}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        )
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 350,
          }}
        >
          <Typography color="text.secondary" variant="body1">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ProductTimeSeriesChart;
